/**
 * PHARMAKON — Journal des achats : CONFIG côté web (cross-module X XWP-26, XMD-03-09).
 *
 * Les valeurs du contrat de liste (longueur de l'aperçu, points de suspension, sujet et clé
 * d'invalidation) viennent du jumeau partagé `@pharmakon/shared/constants/achat-journal.js`,
 * lui-même vérifié contre le CONFIG serveur (`apps/server/src/__tests__/achat-journal.parity.test.js`) —
 * jamais retapées ici (leçon PK-BUG-15). Ce fichier n'ajoute que la présentation de l'aperçu.
 */
import {
  ONGLET_JOURNAL_ACHATS,
  FILTRE_ECART_ACHAT,
  ETAT_ECART_TTC,
  GROUPEMENT_LIGNES_ACHAT,
  PAGINATION_LIGNES_ACHAT,
} from "@pharmakon/shared/constants/achat-journal.js";

export {
  ACHAT_NOTE_APERCU_MAX,
  ACHAT_NOTE_ELLIPSE,
  INVALIDATION_ACHATS,
  cleAchat,
} from "@pharmakon/shared/constants/achat-journal.js";

/** 02_DESIGN §2.1 — la bulle d'aperçu de la note sur une ligne du journal. */
export const NOTE_APERCU_UI = Object.freeze({
  /** largeur maximale de la bulle (px) — au-delà, le texte passe à la ligne */
  largeur_max_px: 360,
  /** marge entre l'icône et la bulle, et entre la bulle et le bord de la fenêtre (px) */
  ecart_px: 8,
});

// ─── Refonte WP-52 — S4 Journal des achats, S5 Journal détaillé (ux/02 §7, §8) ─────────────────
// The vocabulary is the shared twin of the server CONFIG (parity test on the server); the French
// copy of the two screens lives here, verbatim from ux/02.

export { ONGLET_JOURNAL_ACHATS, FILTRE_ECART_ACHAT, ETAT_ECART_TTC, GROUPEMENT_LIGNES_ACHAT, PAGINATION_LIGNES_ACHAT };

/** S4 tabs, in order (ux/02 §7.1). */
export const ONGLETS_JOURNAL = Object.freeze([
  { value: ONGLET_JOURNAL_ACHATS.TOUS, label: "Tous" },
  { value: ONGLET_JOURNAL_ACHATS.BROUILLONS, label: "Brouillons" },
  { value: ONGLET_JOURNAL_ACHATS.VALIDEES, label: "Validées" },
  { value: ONGLET_JOURNAL_ACHATS.A_REGLER, label: "À régler" },
  { value: ONGLET_JOURNAL_ACHATS.ECARTS_A_TRAITER, label: "Écarts à traiter" },
]);

/** S4 « Écart » filter (ux/02 §7.2). */
export const OPTIONS_FILTRE_ECART = Object.freeze([
  { value: FILTRE_ECART_ACHAT.AVEC_ECART, label: "Avec écart" },
  { value: FILTRE_ECART_ACHAT.AVOIR_FINANCIER, label: "Avoir financier à créer" },
  { value: FILTRE_ECART_ACHAT.MANQUANTS, label: "Manquants" },
]);

/** S5 « Regrouper par » (ux/02 §8.3). */
export const OPTIONS_GROUPEMENT = Object.freeze([
  { value: GROUPEMENT_LIGNES_ACHAT.AUCUN, label: "Aucun" },
  { value: GROUPEMENT_LIGNES_ACHAT.FOURNISSEUR, label: "Fournisseur" },
  { value: GROUPEMENT_LIGNES_ACHAT.PRODUIT, label: "Produit" },
  { value: GROUPEMENT_LIGNES_ACHAT.FACTURE, label: "Facture" },
  { value: GROUPEMENT_LIGNES_ACHAT.LABORATOIRE, label: "Laboratoire / marque" },
]);

/** S4 page size (the journal's existing 20 rows per page). */
export const LIMITE_JOURNAL_ACHATS = 20;

/** S4 copy (ux/02 §7; the empty / error texts are AchatDashboard's, kept verbatim). */
export const M_S4 = Object.freeze({
  titre: "Journal des achats",
  sousTitre: "Factures fournisseurs · brouillons et factures validées",
  efactures: "E-factures reçues",
  exporter: "Exporter",
  nouvelAchat: "Nouvel achat",
  grille: "Journal des achats",
  recherche: "N° BL, N° facture, fournisseur…",
  colonnes: {
    note: "Note",
    numeros: "N° interne / N° fourn.",
    fournisseur: "Fournisseur",
    date: "Date",
    type: "Type",
    magasin: "Magasin",
    ttc: "TTC",
    reglement: "Règlement",
    ecart: "Écart",
    statut: "Statut",
  },
  connecte: "Connecté",
  psychotrope: "Psy.",
  reste: (montant) => `reste ${montant}`,
  brouillons: (n) => `${n} achat${n > 1 ? "s" : ""} en brouillon`,
  brouillonsMontant: (montant) => `représentant ${montant} DA. À compléter et valider.`,
  pied: ({ factures, ttc, reste }) => `${factures} facture${factures > 1 ? "s" : ""} · TTC ${ttc} DA · reste dû ${reste} DA`,
  chargement: "Récupération des achats.",
  erreur: "Achats indisponibles",
  videTitre: "Aucun achat à afficher",
  videFiltre: "Aucun achat ne correspond à vos filtres.",
  videAucun: "Créez un nouvel achat manuellement, scannez une vignette ou importez une e-facture.",
  reinitialiser: "Réinitialiser les filtres",
  // Refonte WP-64 (D-26) — the row action and Suppr on a validated facture (M-S1-55 for the dialog).
  supprimerBrouillon: "Supprimer le brouillon",
  suppressionRefusee: "Seul un brouillon peut être supprimé : une facture validée se corrige par rectification ou par avoir.",
  ecart: {
    [ETAT_ECART_TTC.NON_SAISI]: "Total TTC fournisseur non saisi",
    [ETAT_ECART_TTC.CONFORME]: "Conforme",
    [ETAT_ECART_TTC.AVOIR_FINANCIER]: (montant) => `Avoir financier à créer : +${montant} DA`,
    [ETAT_ECART_TTC.INFERIEUR]: (montant) => `Total fournisseur inférieur de ${montant} DA — vérifiez la saisie`,
  },
  filtres: {
    statut: "Statut",
    typePiece: "Type pièce",
    typeAchat: "Type d'achat",
    fournisseur: "Fournisseur",
    magasin: "Magasin",
    periode: "Période",
    montant: "Montant TTC",
    reglement: "Règlement",
    ecart: "Écart",
    psychotrope: "Psychotrope",
    tags: "Tags",
  },
});

/** S5 copy (ux/02 §8). */
export const M_S5 = Object.freeze({
  titre: "Journal détaillé des achats",
  sousTitre: "Une ligne par produit acheté — ce que nous avons acheté, à qui, à quel prix, ce qui est arrivé",
  grille: "Lignes d'achat",
  imprimer: "Imprimer",
  exporter: "Exporter",
  recherche: "Produit, DCI, labo, N° lot, N° facture…",
  regrouper: "Regrouper par",
  colonnes: {
    facture: "Facture",
    produit: "Produit",
    quantite: "Qté",
    ug: "UG",
    recue: "Reçue",
    puHt: "PU HT",
    ristourne: "Ristourne",
    totalHt: "Total HT",
    tva: "TVA",
    ppa: "PPA",
    marge: "Marge",
  },
  lot: (numero, peremption) => [numero ? `Lot ${numero}` : null, peremption].filter(Boolean).join(" · "),
  manque: (n) => `▲ −${n}`,
  manqueTitre: (n) => `${n} unité${n > 1 ? "s" : ""} non reçue${n > 1 ? "s" : ""}`,
  ug: (n) => `+${n}`,
  sousTotal: ({ lignes, quantite, totalHt }) => `${lignes} ligne${lignes > 1 ? "s" : ""} · Qté ${quantite} · Total HT ${totalHt} DA`,
  pied: ({ lignes, quantite, ug, recue, totalHt, valeurPpa, marge }) =>
    [
      `${lignes} ligne${lignes > 1 ? "s" : ""}`,
      `Qté ${quantite}`,
      `UG ${ug}`,
      `Reçue ${recue}`,
      `Total HT ${totalHt} DA`,
      `Valeur PPA ${valeurPpa} DA`,
      marge == null ? null : `Marge prév. moyenne ${marge} %`,
    ]
      .filter(Boolean)
      .join(" · "),
  avantRemise: "avant remise globale",
  vide: "Aucune ligne d'achat ne correspond à ces critères.",
  effacer: "Effacer les filtres",
  erreur: "Journal indisponible.",
  reessayer: "Réessayer",
  sansFournisseur: "Sans fournisseur",
  filtres: {
    periode: "Période",
    fournisseur: "Fournisseur",
    typeProduit: "Type de produit",
    typeAchat: "Type d'achat",
    laboratoire: "Laboratoire / marque",
    magasin: "Magasin",
    numeroLot: "N° lot",
    ecartsQuantite: "Écarts de quantité seulement",
    avecUg: "Avec UG seulement",
    psychotropes: "Psychotropes",
    reglement: "Statut de règlement",
    brouillons: "Inclure les brouillons",
  },
  impression: {
    titre: "Journal détaillé des achats",
    periode: (du, au) => (du || au ? `Période : ${du ?? "…"} – ${au ?? "…"}` : "Toutes périodes"),
    filtres: (n) => (n > 0 ? `${n} filtre${n > 1 ? "s" : ""} actif${n > 1 ? "s" : ""}` : "Sans filtre"),
    tronque: (n) => `Impression limitée aux ${n} premières lignes — exportez en CSV pour la totalité.`,
    preparation: "Préparation de l'impression…",
    echec: "L'impression n'a pas pu être préparée. Réessayez.",
  },
});

/**
 * S5 print: lines read for one printed journal (pages of `PAGINATION_LIGNES_ACHAT.LIMIT_MAX`), a
 * bound of the browser print, not a business rule; beyond it the print says so and points to the
 * CSV export (which carries up to 10 000 rows).
 */
export const IMPRESSION_LIGNES_MAX = 2000;

/** Period presets of the journals' « Période » filter (ux/02 §8.3). */
export const PRESETS_PERIODE = Object.freeze([
  { id: "aujourdhui", label: "Aujourd'hui" },
  { id: "sept_jours", label: "7 jours" },
  { id: "ce_mois", label: "Ce mois" },
  { id: "mois_precedent", label: "Mois précédent" },
  { id: "cette_annee", label: "Cette année" },
]);
